import moment from 'moment';

/**
 * date-helper.js
 * Formats a single date string into a user-friendly dashboard format.
 * @param {String|Date} dateSource - The date to format.
 * @returns {String} Formatted string (e.g., "Today at 12:00 PM", "Aug 1, 2026 at 11:59 PM").
 */
export function formatDate(dateSource) {
    if (!dateSource) return '—';

    const targetDate = moment(dateSource);
    const today = moment();
    const tomorrow = moment().add(1, 'day');
    const timeStr = targetDate.format('h:mm A');

    if (targetDate.isSame(today, 'day')) return `Today at ${timeStr}`;
    if (targetDate.isSame(tomorrow, 'day')) return `Tomorrow at ${timeStr}`;
    
    // Explicit year check: handles both past and future years cleanly
    if (targetDate.year() === today.year()) {
        return `${targetDate.format('MMM D')} at ${timeStr}`;
    }
    return `${targetDate.format('MMM D, YYYY')} at ${timeStr}`;
}

export function formatCreatedDate(dateSource) {
    if (!dateSource) return '—';

    const created = moment(dateSource);
    const now = moment();

    // 1. Calendar year cross-check
    if (created.year() < now.year()) {
        return `${created.format('MMM D, YYYY')}`; // Guarantees year displays for ALL past years
    }

    // 2. Direct time interval differences for the current year
    const diffMonths = now.diff(created, 'months');
    const diffDays = now.diff(created, 'days');
    const diffHours = now.diff(created, 'hours');
    const diffMinutes = now.diff(created, 'minutes');

    // 3. Fallback priority chain
    if (diffMonths > 0) {
        return `${created.format('MMM D')}`; // Year omitted safely because it is the current year
    }
    if (diffDays > 0) {
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    }
    if (diffHours > 0) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    }
    if (diffMinutes > 0) {
        return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    return 'Just now';
}

/**
 * Parses raw backend date strings and forces them to be interpreted as Manila Time (+08:00)
 * @param {String} rawDateStr 
 * @returns {moment.Moment}
 */
export function parseToManilaTime(rawDateStr) {
    if (!rawDateStr) return moment();
    // Replaces space with 'T' and drops everything after a plus sign to format securely as +08:00
    const cleanStr = rawDateStr.replace(' ', 'T').split('+')[0] + '+08:00';
    return moment(cleanStr);
}

/**
 * Formats a pre-parsed Manila Time moment object into a localized time string with the correct timezone label appended
 * @param {moment.Moment} dateInstance - The moment date instance (must be pre-parsed via parseToManilaTime)
 * @param {String} customFormat - Optional override format (e.g. 'h:mm A')
 * @returns {String} e.g., "4:45 PM Manila Time" or "on Oct 12 at 2:00 AM Singapore Time"
 */
export function formatWithLocalTimezone(dateInstance, customFormat = null) {
    if (!dateInstance) return '—';
    
    const now = moment();
    
    // Choose pattern base if not explicitly overridden by the consumer
    const formatPattern = customFormat || 
        (dateInstance.isSame(now, 'day') ? 'h:mm A' : '[on] MMM D [at] h:mm A');
    
    let formattedText = dateInstance.format(formatPattern);

    // Dynamic Timezone Label Generation
    const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Manila';
    let zoneLabel = '';
    
    if (clientTimezone === 'Asia/Manila' || clientTimezone === 'Asia/Singapore') {
        zoneLabel = ' Manila Time';
    } else {
        const cleanCityName = clientTimezone.split('/').pop().replace('_', ' ');
        zoneLabel = cleanCityName && cleanCityName !== clientTimezone
            ? ` ${cleanCityName} Time` 
            : ` (${dateInstance.format('z') || 'Local Time'})`;
    }

    return formattedText + zoneLabel;
}